import type React from "react";

import { faGlobe } from "@fortawesome/pro-regular-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { ListItemIcon, Stack, Typography } from "@mui/material";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import styled from "styled-components";

import { MarketLoginSelectorSE, MarketLoginSelectorUK } from "assets";
import { DropDownButton } from "routes/patients/PatientProfile/components/PatientHeader/PatientHeaderInfo/components/DropdownButton";
import { getActionButtonColorStyles } from "shared/atoms/BaseButton/BaseButton";
import { getAppMarket } from "utils/market";
import { setStorageValue } from "utils/storage";

type MarketLogin = {
  icon: JSX.Element;
  label: "login.market_selector.se" | "login.market_selector.uk";
  language: string;
  type: "email" | "bankid";
  value: "SE" | "UK";
};

const marketLogins: MarketLogin[] = [
  {
    icon: <MarketLoginSelectorSE />,
    label: "login.market_selector.se",
    language: "sv-SE",
    type: "bankid",
    value: "SE",
  },
  {
    icon: <MarketLoginSelectorUK />,
    label: "login.market_selector.uk",
    language: "en-GB",
    type: "email",
    value: "UK",
  },
];

export const MarketLoginSelector: React.FC = () => {
  const navigate = useNavigate();
  const { i18n, t } = useTranslation();

  const defaultAppMarket = getAppMarket() || "SE";
  const defaultMarketLogin = marketLogins.find(({ value }) => value === defaultAppMarket);

  const options = marketLogins.map(login => {
    return {
      children: (
        <Stack spacing={1} direction="row" alignItems="center">
          <StyledListItemIcon>{login.icon}</StyledListItemIcon>
          <Typography variant="subtitle1">{t(login.label)}</Typography>
        </Stack>
      ),
      onClick: () => {
        i18n.changeLanguage(login?.language);
        setStorageValue("language", login?.language as string);
        navigate(`/login/${login?.type}`);
      },
    };
  });

  return (
    <StyledDropdownButton id="calendar-filters" options={options} border={false} dropdownOrigin="left">
      <FontAwesomeIcon icon={faGlobe} />
      {defaultMarketLogin?.label && <Typography variant="subtitle1">{t(defaultMarketLogin.label)}</Typography>}
    </StyledDropdownButton>
  );
};

const StyledListItemIcon = styled(ListItemIcon)`
  &.MuiListItemIcon-root {
    display: flex;
    min-width: auto;
    margin-right: 8px;
  }
`;

const StyledDropdownButton = styled(DropDownButton)`
  height: 40px;
  border-radius: 8px;
  box-sizing: border-box;
  flex: 0;

  ${getActionButtonColorStyles(true)}

  #DropdownButton {
    background: none;
    padding: 0 16px;
    text-align: left;
    white-space: nowrap;
  }

  &:hover {
    background: ${({ theme }) => theme.colors.redesign.b10} !important;

    #DropdownButton > svg > path {
      fill: ${({ theme }) => theme.colors.redesign.b100};
    }

    h6 {
      color: ${({ theme }) => theme.colors.redesign.b100};
    }
  }
`;
